// EmployeeProfile.js
import useEmployeeProfileLogic from 'hooks/useProfile';
import EmployeeNavigation from 'layouts/employeeNavigation';
import Icon from '../../components/appIcon';
import Button from '../../components/ui/button';
import EmployeePhotoCard from './components/employeePhotoCard';

const EmployeeProfile = () => {
  // Use the custom hook to get all the state and functions
  const {
    isCollapsed,
    activeTab,
    isLoading,
    notification,
    departments,
    loadingDepartments,
    employee,
    isAdmin,
    currentUserRole,
    employeeId,
    tabs,
    availableTabs,
    ActiveTabComponent,
    setIsCollapsed,
    setActiveTab,
    setEmployee,
    fetchDepartments,
    loadEmployee,
    handleUpdateProfile,
    handleStatusChange,
    showNotification,
    handleToggleCollapse,
    user
  } = useEmployeeProfileLogic();

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <EmployeeNavigation />
        <div className="pt-16 flex items-center justify-center min-h-[calc(100vh-4rem)]">
          <div className="flex flex-col items-center space-y-4">
            <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
            <p className="text-sm text-muted-foreground">Loading dashboard...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Notifications */}
      {notification && (
        <div className={`fixed top-4 right-4 z-1020 p-4 rounded-lg shadow-lg border 
          ${notification.type === 'success'
            ? 'bg-success text-success-foreground border-success'
            : notification.type === 'error'
              ? 'bg-error text-error-foreground border-error'
              : 'bg-card text-card-foreground border-border'
          }`}>
          <div className="flex items-center space-x-2">
            <Icon
              name={notification.type === 'success'
                ? 'CheckCircle'
                : notification.type === 'error'
                  ? 'XCircle'
                  : 'Info'}
              size={16}
            />
            <span className="text-sm font-medium">{notification.message}</span>
          </div>
        </div>
      )}

      {/* Sidebar */}
      <EmployeeNavigation
        user={user}
        isCollapsed={isCollapsed}
        onToggleCollapse={handleToggleCollapse}
      />

      <main className={`pt-16 transition-all duration-200 ease-in-out ${isCollapsed ? 'lg:pl-16' : 'lg:pl-64'}`}>
        <div className='p-6 space-y-6'>
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold text-foreground">Employee Profile</h1>
              <p className="text-muted-foreground mt-1">Manage employee information and view attendance records</p>
            </div>
            <div className="flex items-center space-x-3 mt-4 sm:mt-0">
              <Button
                variant="outline"
                size="sm"
                iconName="ArrowLeft"
                iconPosition="left"
                onClick={() => navigate('/employee-profile')}
              >
                Back to List
              </Button>
              {isAdmin && (
                <Button variant="default" size="sm" iconName="FileText" iconPosition="left">
                  Generate Report
                </Button>
              )}
            </div>
          </div>

          {/* Content */}
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            {/* Left - Photo & Info */}
            <div className="lg:col-span-1">
              <EmployeePhotoCard
                employee={employee}
                setEmployee={setEmployee}
                onImageUpload={(file, position) => handleUpdateProfile({ files: [file] }, position)}
                onStatusChange={handleStatusChange}
                isAdmin={isAdmin}
              />
            </div>

            {/* Right - Tabs */}
            <div className="lg:col-span-3">
              <div className="bg-card rounded-lg border border-border">
                <div className="border-b border-border">
                  <nav className="flex space-x-1 p-1" role="tablist">
                    {availableTabs.map((tab) => (
                      <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={`flex items-center space-x-2 px-4 py-3 text-sm font-medium rounded-md
                          transition-colors duration-150
                          ${activeTab === tab.id
                            ? 'bg-primary text-primary-foreground shadow-sm'
                            : 'text-muted-foreground hover:text-foreground hover:bg-muted'}`}
                        role="tab"
                        aria-selected={activeTab === tab.id}
                      >
                        <Icon name={tab.icon} size={16} />
                        <span className="hidden sm:inline">{tab.label}</span>
                      </button>
                    ))}
                  </nav>
                </div>

                <div className="p-6" role="tabpanel">
                  {ActiveTabComponent && (
                    <ActiveTabComponent
                      employee={employee}
                      onSave={activeTab === 'personal'
                        ? (data) => handleUpdateProfile({ data })
                        : undefined}
                      isAdmin={isAdmin}
                      isEditable={isAdmin || activeTab === 'personal'}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default EmployeeProfile;